import { Component } from '@theme/component';

/**
 * @typedef {Object} AccordionViewAllRefs
 * @property {HTMLButtonElement} toggle - The button that expands/collapses the extra rows
 */

/**
 * Collapses an accordion to a fixed number of visible rows and exposes a
 * "View all" / "Show less" toggle when there are more rows than the limit.
 *
 * The toggle and the row-hiding are applied entirely in JavaScript so that,
 * without JS, every row stays visible and no button is shown.
 *
 * @extends {Component<AccordionViewAllRefs>}
 */
class AccordionViewAll extends Component {
  requiredRefs = ['toggle'];

  /** @type {HTMLElement[]} */
  #rows = [];

  /** @type {number} */
  get #visibleCount() {
    return parseInt(this.dataset.visibleCount ?? '5', 10) || 5;
  }

  connectedCallback() {
    super.connectedCallback();

    const accordion = this.querySelector('.accordion');
    if (!accordion) return;

    this.#rows = /** @type {HTMLElement[]} */ (Array.from(accordion.querySelectorAll(':scope > accordion-custom')));

    if (this.#rows.length <= this.#visibleCount) {
      // Not enough rows to collapse — keep everything visible and hide the button.
      this.refs.toggle?.classList.add('hidden');
      return;
    }

    this.refs.toggle?.classList.remove('hidden');
    this.#collapse();
  }

  /**
   * Hides rows beyond the visible count and marks the last visible row.
   */
  #collapse() {
    this.#rows.forEach((row, index) => {
      row.classList.toggle('hidden', index >= this.#visibleCount);
      row.classList.remove('is-last-visible');
    });

    this.#rows[this.#visibleCount - 1]?.classList.add('is-last-visible');
    this.#setExpanded(false);
  }

  /**
   * Reveals every row.
   */
  #expand() {
    this.#rows.forEach((row) => {
      row.classList.remove('hidden');
      row.classList.remove('is-last-visible');
    });

    this.#setExpanded(true);
  }

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.dataset.expanded = expanded ? 'true' : 'false';
    this.refs.toggle?.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  /**
   * Toggles between the collapsed and expanded states.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    if (this.dataset.expanded === 'true') {
      this.#collapse();
    } else {
      this.#expand();
    }
  };
}

if (!customElements.get('accordion-view-all')) {
  customElements.define('accordion-view-all', AccordionViewAll);
}
